/**
 * MapLegend: floating at-a-glance map key (bottom-left, the cartographic
 * convention).
 *
 * Colors come from `resolveDistrictColors` / `resolveDistrictLabels` -- the
 * same resolvers the map fill expression and the stats panel use -- so the
 * map, legend, and stats can never disagree, even when the tenant config
 * supplies fewer colors than districts.
 *
 * Collapsible to a small chip; the collapse state persists per session.
 * The district stats panel keeps its own per-row chips; this is the
 * at-a-glance key, no dedup gymnastics between the two.
 */
import { useState } from 'react';
import { resolveDistrictColors, resolveDistrictLabels } from '../../lib/districtColors';

export interface LegendItem {
  color: string;
  label: string;
}

/** Build legend items for a district map: one per district. */
export function districtLegendItems(
  districtCount: number,
  colors: readonly string[] | undefined,
  labels: readonly string[] | undefined,
): LegendItem[] {
  const resolvedColors = resolveDistrictColors(districtCount, colors);
  const resolvedLabels = resolveDistrictLabels(districtCount, labels);
  return resolvedColors.map((color, i) => ({
    color,
    label: resolvedLabels[i] ?? `District ${i + 1}`,
  }));
}

/**
 * Chip color approximating the map's unassigned fill
 * (`rgba(15, 23, 42, 0.04)` composited over a white basemap).
 */
export const UNASSIGNED_LEGEND_ITEM: LegendItem = {
  color: '#f1efec',
  label: 'Unassigned',
};

function readCollapsed(storageKey: string): boolean {
  try {
    return window.sessionStorage.getItem(`${storageKey}:collapsed`) === '1';
  } catch {
    return false;
  }
}

export function MapLegend({
  items,
  storageKey = 'map-legend',
  className = '',
}: {
  items: LegendItem[];
  /** Distinct per map instance so collapsing one map doesn't collapse others. */
  storageKey?: string;
  className?: string;
}): JSX.Element {
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed(storageKey));

  function toggle(): void {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.sessionStorage.setItem(`${storageKey}:collapsed`, next ? '1' : '0');
      } catch {
        // Session storage unavailable -- the toggle still works for this view.
      }
      return next;
    });
  }

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-expanded={false}
        aria-label="Show map legend"
        className={`floating inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-white ${className}`}
      >
        Legend
      </button>
    );
  }

  return (
    <div
      className={`floating w-44 ${className}`}
    >
      <div className="flex items-center justify-between px-3 pt-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          Legend
        </h2>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={true}
          aria-label="Hide map legend"
          className="rounded px-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
        >
          <span aria-hidden="true">▾</span>
        </button>
      </div>
      <ul className="max-h-56 space-y-1.5 overflow-y-auto px-3 pb-3 pt-1.5">
        {items.map((item) => (
          <li key={item.label} className="flex items-center gap-2 text-xs text-stone-700">
            <span
              aria-hidden="true"
              className="inline-block h-3.5 w-3.5 shrink-0 rounded-sm border border-black/15"
              style={{ backgroundColor: item.color }}
            />
            <span className="truncate">{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
